/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import WordDisplay from './WordDisplay';
import ScoreBoard from './ScoreBoard';
import Recorder from './Recorder';

function GameScreen({ level, wordCount, autoRec, onGameOver }) {
  // Estados
  const [gameState, setGameState] = useState({
    words: [],  // Inicializado como array vazio
    currentWordIndex: 0,
    score: 0,
    timeElapsed: 0,
    loading: false,
    gameEnded: false,
    isFinishing: false,
    isLastWord: false,
    error: null
  });
  const [isFinalizingAudio, setIsFinalizingAudio] = useState(false);
  const [isProcessingEnd, setIsProcessingEnd] = useState(false);
  const [recordingState, setRecordingState] = useState({
    isEnabled: true,
    hasPermission: false
  });

  // Refs
  const gameRef = useRef({
    timerInterval: null,
    finalData: null,
    hasFinished: false,
    isProcessingEnd: false
  });
  const recorderRef = useRef(null);

  // Funções utilitárias
  const getPointsPerWord = useCallback(() => {
    return 10;
  }, []);

  const updateGameState = useCallback((newState) => {
    setGameState(prevState => ({
      ...prevState,
      ...newState
    }));
  }, []);

  const finishGameWithoutAudio = useCallback(() => {
    if (!gameRef.current.finalData) {
      console.log("Dados finais não disponíveis");
      return;
    }

    const { finalScore, timeElapsed, wordsList } = gameRef.current.finalData;
    gameRef.current.hasFinished = true;
    onGameOver(finalScore, timeElapsed, null, wordsList);
  }, [onGameOver]);

// No GameScreen.jsx, na função handleAudioUrl
const handleAudioUrl = useCallback((audioUrl) => {
    console.log("Processando áudio final:", audioUrl);

    if (!gameRef.current.finalData) {
        console.log("Ignorando áudio - dados finais não disponíveis");
        return;
    }

    const { finalScore, timeElapsed, wordsList } = gameRef.current.finalData;

    console.log("Finalizando jogo com áudio:", {
        score: finalScore,
        timeElapsed,  // Usando timeElapsed consistentemente
        wordsCount: wordsList.length,
        isFinalizingAudio
    });

    gameRef.current.hasFinished = true;
    onGameOver(finalScore, timeElapsed, audioUrl, wordsList);  // Garantindo que passamos timeElapsed
    setIsFinalizingAudio(false);
    setIsProcessingEnd(false);
}, [onGameOver, isFinalizingAudio]);

  useEffect(() => {
    if (gameState.isFinishing) {
      console.log("Estado de finalização:", {
        isFinalizingAudio,
        isProcessingEnd,
        hasFinished: gameRef.current.hasFinished
      });
    }
  }, [gameState.isFinishing, isFinalizingAudio, isProcessingEnd]);

  const endGame = useCallback(() => {
    if (isFinalizingAudio || gameState.isFinishing || gameState.gameEnded) {
      console.log("Jogo já está em processo de finalização");
      return;
    }

    console.log("Iniciando processo de finalização do jogo");

    const finalScore = gameState.score + getPointsPerWord();
    const wordsList = gameState.words.map(w => w.verbete || w);

    gameRef.current.finalData = {
      finalScore,
      timeElapsed: gameState.timeElapsed,
      wordsList
    };

    // Primeiro atualize o estado do jogo
    updateGameState({
      isFinishing: true,
      gameEnded: true
    });

    // Então pare o timer
    if (gameRef.current.timerInterval) {
      clearInterval(gameRef.current.timerInterval);
      gameRef.current.timerInterval = null;
    }

    // Defina isFinalizingAudio antes de parar a gravação
    setIsFinalizingAudio(true);
    setIsProcessingEnd(true);

    if (!recordingState.isEnabled || !recordingState.hasPermission) {
      console.log("Finalizando sem gravação");
      finishGameWithoutAudio();
      return;
    }

    if (recorderRef.current) {
      console.log("Parando gravação final");
      try {
        recorderRef.current.stopRecording();
      } catch (error) {
        console.error("Erro ao parar gravação:", error);
        finishGameWithoutAudio();
      }
    } else {
      console.log("Recorder não disponível");
      finishGameWithoutAudio();
    }
  }, [
    gameState,
    getPointsPerWord,
    recordingState.isEnabled,
    recordingState.hasPermission,
    finishGameWithoutAudio,
    updateGameState,
    isFinalizingAudio
  ]);

  // Configuração inicial do jogo
  const initializeGame = useCallback(async () => {
    updateGameState({
      loading: true,
      error: null,
      words: [],
      currentWordIndex: 0,
      score: 0,
      timeElapsed: 0,
      isLastWord: false,
      gameEnded: false,
      isFinishing: false
    });

    try {
      const apiLevel = {
        'facil': 'easy',
        'medio': 'medium',
        'dificil': 'hard'
      }[level] || 'easy';

      const response = await fetch('https://api.juntaletras.com.br/api/v1/words/list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: apiLevel,
          Length: wordCount
        })
      });

      if (!response.ok) {
        throw new Error(`Erro na API: ${response.status}`);
      }

      const data = await response.json();
      const words = Array.isArray(data) ? data : (data.words || []);

      updateGameState({
        words,
        loading: false,
        error: null
      });

      startGameTimer();

    } catch (error) {
      console.error('Erro ao inicializar jogo:', error);
      updateGameState({
        error: error.message,
        loading: false,
        words: []
      });
    }
  }, [level, wordCount, updateGameState]);

  // Timer do jogo
  const startGameTimer = useCallback(() => {
    if (gameRef.current.timerInterval) {
      clearInterval(gameRef.current.timerInterval);
    }

    gameRef.current.timerInterval = setInterval(() => {
      if (!gameState.gameEnded && !gameState.isFinishing) {
        updateGameState(prev => ({
          timeElapsed: prev.timeElapsed + 1
        }));
      }
    }, 1000);
  }, [gameState.gameEnded, gameState.isFinishing, updateGameState]);

  const advanceWord = useCallback(() => {
    if (gameState.loading || gameState.gameEnded || gameState.isFinishing) {
      console.log("Ignorando avanço - jogo em estado inválido");
      return;
    }

    if (gameState.currentWordIndex >= gameState.words.length - 1) {
      console.log("Última palavra alcançada, iniciando finalização");
      endGame();
      return;
    }

    const newScore = gameState.score + getPointsPerWord();
    const newIndex = gameState.currentWordIndex + 1;
    const isLast = newIndex === gameState.words.length - 1;

    updateGameState({
      score: newScore,
      currentWordIndex: newIndex,
      isLastWord: isLast
    });

    if (isLast) {
      console.log("Próxima palavra será a última");
    }
  }, [gameState, getPointsPerWord, updateGameState, endGame]);

  // Effects
  useEffect(() => {
    initializeGame();
    return () => {
      if (gameRef.current.timerInterval) {
        clearInterval(gameRef.current.timerInterval);
        gameRef.current.timerInterval = null;
      }

      if (recorderRef.current && !gameRef.current.hasFinished) {
        try {
          recorderRef.current.stopRecording();
        } catch (error) {
          console.error("Erro ao limpar gravação:", error);
        }
      }

      gameRef.current = {
        hasFinished: true,
        isProcessingEnd: false,
        finalData: null,
        timerInterval: null
      };
    };
  }, [initializeGame]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !gameState.gameEnded && !gameState.loading && !gameState.isFinishing) {
        e.preventDefault();
        advanceWord();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [advanceWord, gameState]);

  useEffect(() => {
    if (!autoRec) {
      setRecordingState(prev => ({ ...prev, isEnabled: false }));
      return;
    }

    async function checkMicPermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        setRecordingState(prev => ({ ...prev, hasPermission: true }));
      } catch (err) {
        console.log("Erro de permissão do microfone:", err);
        setRecordingState(prev => ({
          ...prev,
          hasPermission: false,
          isEnabled: false
        }));
      }
    }

    checkMicPermission();
  }, [autoRec]);

  // Renderização condicional
  if (gameState.loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Carregando palavras...</p>
      </div>
    );
  }

  if (gameState.error && gameState.words.length === 0) {
    return (
      <div className="error-screen">
        <h2>Erro ao carregar palavras</h2>
        <p>{gameState.error}</p>
        <button onClick={initializeGame}>Tentar Novamente</button>
      </div>
    );
  }

  const currentWord = gameState.words[gameState.currentWordIndex]?.verbete || '';

  return (
    <div>
      <div className="bgb"></div>
      <div className="bgb bg2"></div>
      <div className="bgb bg3"></div>
      <div className="game-screen content">
        {isProcessingEnd && (
          <div className="processing-overlay">
            <div className="spinner"></div>
            <p>Finalizando jogo...</p>
          </div>
        )}
        <ScoreBoard
          score={gameState.score}
          timeElapsed={gameState.timeElapsed}
        />
        <WordDisplay word={currentWord} />

        {recordingState.isEnabled && (
          <div style={{ border: '2px solid red', margin: '10px' }}>
            <Recorder
              ref={recorderRef}
              sendAudioUrlToParent={handleAudioUrl}
              autoRec={autoRec}
              isGameActive={!gameState.gameEnded && !gameState.isFinishing}
              onStopRecording={() => {
                console.log("Estado atual do jogo:", {
                  isFinishing: gameState.isFinishing,
                  gameEnded: gameState.gameEnded,
                  isFinalizingAudio,
                  isProcessingEnd
                });
              }}
            />
          </div>
        )}

        <div className="game-controls">
          <button
            type="button"
            className="next-word-btn"
            onClick={advanceWord}
            disabled={gameState.gameEnded || gameState.isFinishing}
          >
            {gameState.isLastWord ? "Última Palavra!" : "Próxima Palavra"}
          </button>

          <p className="keyboard-hint">ou pressione a BARRA DE ESPAÇO</p>
        </div>

        <div className="game-info">
          <p>Nível: <strong>{level.toUpperCase()}</strong></p>
          <p>Palavras: {gameState.currentWordIndex + 1} / {gameState.words.length}</p>
        </div>
      </div>
    </div>
  );
}

export default GameScreen;
